import React, { useEffect, Suspense, lazy, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles, CircularProgress, Box } from '@mui/material';
import ResponsiveLayout from './ResponsiveLayout';
import { isMobileDevice } from './utils/device';
// A small data module, so the routes can name a section without loading the page.
import { BLOG_SECTIONS } from './data/blogSections';
import DesktopOnly from './phone/DesktopOnly';
import PhoneWelcomeSheet from './phone/PhoneWelcomeSheet';
import PhoneInstallSheet from './phone/PhoneInstallSheet';
import { captureAcquisition } from './utils/acquisitionTracking';
import ErrorBoundary from './components/ErrorBoundary';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginDialogProvider } from './contexts/LoginDialogContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PRINT_STYLES } from './common/styles/printStyles';
import { applyDocumentBackground, clearDocumentBackground } from './utils/documentBackground';
import { FloatingChatProvider, useFloatingChat } from './contexts/FloatingChatContext';
// FloatingChatAssistant is lazy-loaded below — the FloatingChat barrel re-exports
// hooks/useFileUpload, which transitively forms a TDZ cycle with ErrorBoundary
// during react-refresh HMR if FloatingChat is loaded eagerly. Lazy import breaks
// the cycle (the module graph isn't visited until after App.tsx finishes init).
// OnboardingManager removed - will be replaced with inline form in UserProfile
// import OnboardingManager from './components/OnboardingManager';
import { createLightTheme, createDarkTheme } from './common/styles/universalTheme';
import { useDiscoveryQuizSaver } from './hooks/useDiscoveryQuizSaver';
import { useCreditSync } from './hooks/useCreditSync';
import { useUserPropertiesRefresh } from './hooks/useUserPropertiesRefresh';
import { DiscoveryQuizProvider, useDiscoveryQuiz } from './contexts/DiscoveryQuizContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import DiscoveryQuizModal from './components/discovery/DiscoveryQuizModal';
import PostLoginQuizPrompt from './components/discovery/PostLoginQuizPrompt';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import PostLoginWelcome from './components/discovery/PostLoginWelcome';
import { OnboardingChecklistProvider } from './contexts/OnboardingChecklistContext';
import OnboardingChecklist from './components/onboarding/OnboardingChecklist';
import UpgradeOffer from './components/billing/UpgradeOffer';
import { useLevelUpStore } from './stores/levelUpStore';
import { useNotificationStore } from './stores/notificationStore';

// Lazy load components - Production routes
const LandingPage = lazy(() => import('./LandingPage'));
const AcademyPage = lazy(() => import('./AcademyPage'));
const CrisisPage = lazy(() => import('./pages/CrisisPage'));
const FAQ = lazy(() => import('./FAQ'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./TermsAndConditions'));
const UserProfile = lazy(() => import('./components/UserProfile'));

// New tool pages
const StockResearch = lazy(() => import('./pages/StockResearch'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogArticlePage = lazy(() => import('./pages/BlogArticlePage'));
const UnifiedChat = lazy(() => import('./pages/UnifiedChat'));
const PortfolioHub = lazy(() => import('./pages/PortfolioHub'));
const MyWatchlist = lazy(() => import('./pages/MyWatchlist'));
const AgentBradfordDashboard = lazy(() => import('./AgentBradfordDashboard'));
const BillingReturn = lazy(() => import('./pages/BillingReturn'));
const RebalanceWorkflow = lazy(() => import('./pages/RebalanceWorkflow'));
const MonitoringWorkflow = lazy(() => import('./pages/MonitoringWorkflow'));
const SharedReportPage = lazy(() => import('./pages/SharedReportPage'));
const Plans = lazy(() => import('./pages/Plans'));
const SectorBrowsePage = lazy(() => import('./pages/SectorBrowsePage'));
const ExchangeBrowsePage = lazy(() => import('./pages/ExchangeBrowsePage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const GlossaryTermPage = lazy(() => import('./pages/GlossaryTermPage'));

// Static info pages
const About = lazy(() => import('./About'));
const SecurityPage = lazy(() => import('./Security'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// TeamPage removed - route hidden

// Import test function in development
if (process.env.NODE_ENV === 'development') {
  import('./utils/testFirestore');
}

// Lazy load dev-only components
const ChartVisualTest = lazy(() => import('./components/charts/ChartVisualTest'));

// Admin dashboard (auth + admin-allowlist gated, robots-noindex). Lives
// outside the public Layout so the sidebar nav is full-width.
const IndexAdmin = lazy(() => import('./pages/IndexAdmin'));

// Storyboard — internal design preview. Email-gated to WiseTree team;
// blocked from crawlers in robots.txt.
const StoryboardPage = lazy(() => import('./pages/StoryboardPage'));

// FloatingChatAssistant lazy-loaded to break the HMR TDZ cycle described
// at the import block at the top of this file. The Suspense fallback for
// GlobalFloatingChat is just `null` — the chat widget appearing a frame
// late is invisible UX.
const FloatingChatAssistant = lazy(() => import('./components/FloatingChat/FloatingChatAssistant'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="50vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
      Loading...
    </Box>
  </Box>
);

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Global Floating Chat Component
 *
 * Renders the FloatingChatAssistant globally (once) using Option C: Hybrid Context-Aware Sessions.
 * The chat session persists across page navigations, with dynamic context injection per page.
 */
const GlobalFloatingChat: React.FC = () => {
  const { isVisible } = useFloatingChat();

  // Don't render on /chat page (it's redundant there - full chat UI exists)
  if (!isVisible) return null;

  // Suspense fallback `null` — chat-widget showing one frame late is
  // imperceptible UX, but the lazy import is what fixes the HMR cycle.
  return (
    <Suspense fallback={null}>
      <FloatingChatAssistant showPulse={true} />
    </Suspense>
  );
};

/**
 * Global Discovery Quiz Modal
 *
 * Renders once globally so any component can trigger it via DiscoveryQuizContext.
 */
const GlobalDiscoveryQuiz: React.FC = () => {
  const { isOpen, closeQuiz, source } = useDiscoveryQuiz();
  return <DiscoveryQuizModal open={isOpen} onClose={closeQuiz} source={source} />;
};

// Inner component that has access to theme
const AppContent: React.FC = () => {
  // Persist discovery quiz answers to Firestore after login
  useDiscoveryQuizSaver();

  // Sync IA Credits from Firestore (real-time via onSnapshot) to Zustand
  useCreditSync();

  // Keep Analytics user properties in sync with live store state — so
  // cohort segments (subscription_tier, portfolios_count, level_up_tier,
  // is_power_user, last_active_at) stay accurate over time instead of
  // being frozen at signup.
  useUserPropertiesRefresh();

  // Capture UTM/referrer first-touch attribution on first mount.
  // Persists to localStorage so paid acquisition is attributable across
  // sessions and survives until sign-up.
  useEffect(() => {
    captureAcquisition();
  }, []);

  // Load level-up progress and notifications when user authenticates
  const { user: authUser } = useAuth();
  const loadLevelProgress = useLevelUpStore((s) => s.loadProgress);
  const loadNotifications = useNotificationStore((s) => s.loadNotifications);
  const resetLevelUp = useLevelUpStore((s) => s.reset);
  const resetNotifications = useNotificationStore((s) => s.reset);

  useEffect(() => {
    if (authUser?.uid) {
      loadLevelProgress(authUser.uid);
      loadNotifications(authUser.uid);
    } else {
      resetLevelUp();
      resetNotifications();
    }
  }, [authUser?.uid, loadLevelProgress, loadNotifications, resetLevelUp, resetNotifications]);

  // Desktop experience
  return (
    <>
    <Routes>
      {/* Phone below `md`, desktop otherwise — one switch for every route. */}
      <Route element={<ResponsiveLayout />}>
        {/* Landing Page */}
        <Route
          path="/"
          element={
            <Suspense fallback={<PageLoader />}>
              <LandingPage />
            </Suspense>
          }
        />

        {/* ===== TOOL PAGES ===== */}

        {/* Research — asset-type-aware path-param URLs:
              Canonical:      /research/stock/NVDA  +  /research/etf/SPY
              Short form:     /research/NVDA        (treated as stock; auto-
                              corrects to /research/etf/{symbol} if the
                              symbol resolves to an ETF in profile data —
                              see StockResearch.tsx auto-correction useEffect)
              Landing:        /research              (search-only, no symbol)

            Route patterns use positional params (:p1, :p2) and the page
            component disambiguates in extractResearchParams() based on
            whether :p1 is in {'stock','etf'} or is a symbol. This avoids
            React Router's "two routes match" scoring problem when an
            optional segment overlaps with a single-segment short form.

            Legacy /stock-research/{symbol} 301-redirects at the CDN level
            via firebase.json — see the `redirects` block there. */}

        {/* Sector browse page — /research/sector/{slug}. SectorBrowsePage
            is the dynamic React body that mounts inside the prerendered
            SEO shell. The :p1/:p2 catch-all below would also match (and
            falls back to StockResearch's landing UI) — listing this
            first makes the intent explicit. React Router v6 picks the
            most specific path first regardless of order, but this
            ordering reads more clearly. */}
        <Route
          path="/research/sector/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <SectorBrowsePage />
            </Suspense>
          }
        />
        {/* Exchange browse page — /research/exchange/{slug}. Same pattern
            as SectorBrowsePage above — dynamic React body inside the
            prerendered SEO shell. Adds SectorBreakdown donut +
            MarketHoursWidget (live local-time "open/closed" indicator). */}
        <Route
          path="/research/exchange/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <ExchangeBrowsePage />
            </Suspense>
          }
        />
        <Route
          path="/research/:p1/:p2"
          element={
            <Suspense fallback={<PageLoader />}>
              <StockResearch />
            </Suspense>
          }
        />
        <Route
          path="/research/:p1"
          element={
            <Suspense fallback={<PageLoader />}>
              <StockResearch />
            </Suspense>
          }
        />
        <Route
          path="/research"
          element={
            <Suspense fallback={<PageLoader />}>
              <StockResearch />
            </Suspense>
          }
        />

        {/* Legacy routes — kept for any in-app navigation that hasn't been
            updated to /research/* yet. Edge-level 301 in firebase.json
            handles external traffic before React ever mounts. */}
        <Route
          path="/stock-research/:symbol?"
          element={
            <Suspense fallback={<PageLoader />}>
              <StockResearch />
            </Suspense>
          }
        />
        <Route
          path="/stock-research"
          element={
            <Suspense fallback={<PageLoader />}>
              <StockResearch />
            </Suspense>
          }
        />

        {/* Glossary — /glossary (index) + /glossary/:slug (term page).
            The prerendered shell HTML at build/glossary/{slug}/index.html
            still serves non-JS crawlers with FAQPage schema; React
            mounting now renders dedicated GlossaryPage / GlossaryTermPage
            components so the dev experience and post-mount prod UX match.
        */}
        <Route
          path="/glossary/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <GlossaryTermPage />
            </Suspense>
          }
        />
        <Route
          path="/glossary"
          element={
            <Suspense fallback={<PageLoader />}>
              <GlossaryPage />
            </Suspense>
          }
        />

        {/* HTML sitemap hub — categorized directory of every research
            page. Static prerendered shell at build/sitemap/index.html
            serves crawlers; SitemapPage handles the React mount. */}
        <Route
          path="/sitemap"
          element={
            <Suspense fallback={<PageLoader />}>
              <SitemapPage />
            </Suspense>
          }
        />

        {/* Chat - Unified interface for AI conversations */}
        <Route
          path="/chat"
          element={
            <Suspense fallback={<PageLoader />}>
              <UnifiedChat />
            </Suspense>
          }
        />

        {/* Portfolio Risk Assessment — the Risk Analysis Reports destination,
            with each mode as a PATH rather than a query string (owner,
            2026-08-30): /new, /edit/:id, /run/:id, /rebalance/:id, /manage/:id,
            /from/:source. `riskAssessmentRoutes.ts` is the single place those
            are built and read; the legacy `?compose/?load/?manage` forms are
            still parsed so old bookmarks land. */}
        <Route
          path="/portfolio/risk-assessment/:mode?/:portfolioId?"
          element={
            <Suspense fallback={<PageLoader />}>
              <PortfolioHub />
            </Suspense>
          }
        />

        {/* Portfolio Risk Results - Bradford Dashboard (optional reportId for shareable URLs) */}
        <Route
          path="/portfolio/risk-results/:reportId?"
          element={
            <Suspense fallback={<PageLoader />}>
              <AgentBradfordDashboard />
            </Suspense>
          }
        />

        {/* Section-addressable report (ROADMAP 1.22): `/…/<id>/<section>`.
            Declared AFTER the static `share/:reportId` route below so that a
            path beginning `share` can never be read as a report id — React
            Router v6 ranks static segments above dynamic ones, but the ordering
            makes the intent legible without relying on that ranking. */}
        <Route
          path="/portfolio/risk-results/:reportId/:section"
          element={
            <Suspense fallback={<PageLoader />}>
              <AgentBradfordDashboard />
            </Suspense>
          }
        />

        {/* Legacy shared report redirect → new URL format */}
        <Route
          path="/portfolio/risk-results/share/:reportId"
          element={
            <Suspense fallback={<PageLoader />}>
              <SharedReportPage />
            </Suspense>
          }
        />

        {/* ===== STATIC INFO PAGES ===== */}

        {/* The IndexAlpha Brief, in public (ROADMAP 1.21).
            `/blog` is the CANONICAL first page; `/blog/page/N` is the rest.
            The legacy `/blog/list` shapes from the original proposal are
            deliberately absent — three URLs for one page is a duplicate-content
            problem, not a feature. The article route carries the ISO date so a
            reader (and a crawler) can see the period from the address alone. */}
        <Route
          path="/blog"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage />
            </Suspense>
          }
        />
        <Route
          path="/blog/page/:page"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage />
            </Suspense>
          }
        />
        {/* Section fronts (ROADMAP ASX row). Static segments, declared ABOVE
            `/blog/:date/:slug`, so `international` is never read as a date. */}
        <Route
          path="/blog/international"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage section={BLOG_SECTIONS.international} />
            </Suspense>
          }
        />
        <Route
          path="/blog/international/page/:page"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage section={BLOG_SECTIONS.international} />
            </Suspense>
          }
        />
        <Route
          path="/blog/international/asx"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage section={BLOG_SECTIONS.asx} />
            </Suspense>
          }
        />
        <Route
          path="/blog/international/asx/page/:page"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogIndexPage section={BLOG_SECTIONS.asx} />
            </Suspense>
          }
        />
        <Route
          path="/blog/:date/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <BlogArticlePage />
            </Suspense>
          }
        />

        {/* Crisis pages — a historical crisis replayed on sample portfolios
            (SEO "Problem-solution search", S2.2); prerendered shells at
            build/stress-tests/{slug}/index.html serve crawlers. */}
        <Route
          path="/stress-tests/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <CrisisPage />
            </Suspense>
          }
        />

        {/* Academy */}
        <Route
          path="/academy"
          element={
            <Suspense fallback={<PageLoader />}>
              <AcademyPage />
            </Suspense>
          }
        />

        {/* FAQ */}
        <Route
          path="/faq"
          element={
            <Suspense fallback={<PageLoader />}>
              <FAQ />
            </Suspense>
          }
        />

        {/* About */}
        <Route
          path="/about"
          element={
            <Suspense fallback={<PageLoader />}>
              <About />
            </Suspense>
          }
        />

        {/* Security */}
        <Route
          path="/security"
          element={
            <Suspense fallback={<PageLoader />}>
              <SecurityPage />
            </Suspense>
          }
        />

        {/* Privacy Policy */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />

        {/* Terms & Conditions */}
        <Route
          path="/terms"
          element={
            <Suspense fallback={<PageLoader />}>
              <TermsAndConditions />
            </Suspense>
          }
        />

        {/* Team route removed - page hidden */}

        {/* ===== PROTECTED PAGES ===== */}

        {/* Profile */}
        <Route
          path="/profile"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            </Suspense>
          }
        />

        {/* My Portfolios — consolidated into the risk-assessment hub (one home
            for picking, managing, and analyzing portfolios). */}
        <Route path="/portfolios" element={<Navigate to="/portfolio/risk-assessment" replace />} />

        {/* The rebalance workflow is a DESTINATION as of ROADMAP 3.8 — it used
            to be an anchor at the tail of a risk result, which made a decision
            worth its own page into a footer, and led with a tax-rate field. The
            risk page now carries a button; the work happens here. Both URLs a
            user naturally guesses resolve to it (#131). */}
        {/* Stripe returns here after checkout — success carries ?session_id,
            cancel comes bare. Registered since #136: the backend pointed both
            at /billing from the start; only this SPA never had the page, so a
            paying user's first post-payment screen was the 404. */}
        <Route
          path="/billing"
          element={
            <Suspense fallback={<PageLoader />}>
              <BillingReturn />
            </Suspense>
          }
        />

        {/* `:reportId` optional, matching the risk-results route above: a stored
            comparison is a thing with an address, not a page state expressed in
            a query string (owner, 2026-08-30). */}
        <Route
          path="/portfolio/rebalance/:reportId?"
          element={
            <Suspense fallback={<PageLoader />}>
              <RebalanceWorkflow />
            </Suspense>
          }
        />
        <Route path="/rebalance" element={<Navigate to="/portfolio/rebalance" replace />} />

        {/* The Signals workflow, its own route for the same reason (ROADMAP
            3.8, owner 2026-08-23: "I need them as their own route to their
            relevant page"). Both paid workflows are destinations now — a risk
            result ends with two buttons, not with the work embedded under it. */}
        <Route
          path="/portfolio/signals"
          element={
            <Suspense fallback={<PageLoader />}>
              <MonitoringWorkflow />
            </Suspense>
          }
        />
        <Route path="/portfolio/monitoring" element={<Navigate to="/portfolio/signals" replace />} />
        <Route path="/monitoring" element={<Navigate to="/portfolio/signals" replace />} />

        {/* My Watchlist - symbol tracking page */}
        <Route
          path="/watchlist"
          element={
            <ProtectedRoute>
              <MyWatchlist />
            </ProtectedRoute>
          }
        />

        {/* Settings live on the profile page. Every transactional email's
            "manage preferences" link (`email_compliance.manage_preferences_url`)
            and the newsletter box point at /settings, which had no route and
            showed Not Found ([[BUGS #508]]) — a redirect keeps already-sent
            emails working. */}
        <Route path="/settings" element={<Navigate to="/profile" replace />} />

        {/* Plans & Billing */}
        <Route
          path="/plans"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute>
                <Plans />
              </ProtectedRoute>
            </Suspense>
          }
        />

        <Route
          path="*"
          element={
            <Suspense fallback={<PageLoader />}>
              <NotFoundPage />
            </Suspense>
          }
        />
      </Route>

      {/* Dev-only: Chart Visual Test Page - Outside Layout */}
      <Route
        path="/dev/chart-test"
        element={
          <Suspense fallback={<PageLoader />}>
            <ChartVisualTest />
          </Suspense>
        }
      />

      {/* Admin dashboard — auth-gated + admin-allowlist gated + noindex.
          Outside the public Layout so the sidebar nav is full-width. */}
      {/* `:tabId?` makes each tab directly addressable (/index_admin/email) so a
          tab can be bookmarked or pasted into a message. The gate is unchanged —
          the path carries no authority; an unauthenticated or non-admin visitor
          gets the same rejection whichever segment they typed. */}
      {/* Not on a phone or tablet (ROADMAP 4.17): the route is not registered,
          so the address falls through to the ordinary 404 inside the normal
          layout — as if it did not exist — and the admin bundle is never
          fetched. A layer, not the boundary: the backend's admin check is. */}
      {!isMobileDevice() && (
        <Route
          path="/index_admin/:tabId?"
          element={
            <Suspense fallback={<PageLoader />}>
              <IndexAdmin />
            </Suspense>
          }
        />
      )}

      {/* Storyboard — internal design preview. Email-gated inside the
          component; robots.txt blocks all crawlers. */}
      <Route
        path="/storyboard"
        element={
          <Suspense fallback={<PageLoader />}>
            <StoryboardPage />
          </Suspense>
        }
      />
    </Routes>

    {/* Says once, on a touch phone, what the phone does */}
    <PhoneWelcomeSheet />
    <PhoneInstallSheet />
    {/* Prompts a refresh when a newer build has been deployed */}
    <UpdateAvailableToast />
    {/* The phone reads; it does not converse or onboard (ROADMAP "The phone
        branch", D4). These mount here rather than in a layout, so the phone
        shell alone would still float a chat window and a quiz over the page. */}
    <DesktopOnly>
      {/* Global Floating Chat - Option C: Hybrid Context-Aware Sessions */}
      <GlobalFloatingChat />
      {/* Global Discovery Quiz - triggered from any page via DiscoveryQuizContext */}
      <GlobalDiscoveryQuiz />
      {/* Post-login quiz prompt for users who haven't completed the quiz */}
      <PostLoginQuizPrompt />
      {/* Post-login welcome explainer — shows after quiz-based signup */}
      <PostLoginWelcome />
      {/* Onboarding checklist — guides first watchlist + portfolio setup */}
      <OnboardingChecklist />
    </DesktopOnly>
    {/* Every paywall a path, not a sentence (ROADMAP 0.R R2) — on a phone too. */}
    <UpgradeOffer />
    </>
  );
};

// Theme wrapper component
const ThemedApp: React.FC = () => {
  const { effectiveTheme, densityMode } = useTheme();

  // Create theme based on current mode and density
  const theme = useMemo(() => {
    return effectiveTheme === 'dark'
      ? createDarkTheme(densityMode)
      : createLightTheme(densityMode);
  }, [effectiveTheme, densityMode]);

  // Keep the DOCUMENT background on the active theme ([[BUGS #173]]).
  //
  // Two bugs lived in the three lines this replaces, and they compounded.
  //
  // **`html` was never updated.** The anti-FOUC script in `public/index.html`
  // paints BOTH `documentElement` and `body` from the stored theme at first
  // paint; this effect updated only `body`. `html` therefore kept whatever the
  // page LOADED with and never followed a toggle — and because `html` carries a
  // background, it is what paints the canvas, so `body`'s colour never reaches
  // it. Load in dark, switch to light, and the strip of canvas below a short
  // page stays #1e1e1e under a white page. Measured on staging: `html` and
  // `body` resolved to two different colours at the same moment.
  //
  // **The colours were stale.** `#1a1a1a`/`#ffffff` predate the palette; the
  // boot script's own comment records that it moved to #1e1e1e/#f8f9fa so the
  // flash would match "background.default ... exactly — no second flash", while
  // this effect kept repainting the old pair a frame later. Reading the token
  // means there is one source of truth and it cannot drift again.
  useEffect(() => {
    applyDocumentBackground(theme.palette.background.default);
    return clearDocumentBackground;
  }, [theme]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {/* What the page becomes on paper. Mounted here rather than in a .css
          file because nothing imports those (ROADMAP "Share, not export…"
          item 3). */}
      <GlobalStyles styles={PRINT_STYLES} />
      <AnalyticsTracker />
      {/* FloatingChatProvider implements Option C: Hybrid Context-Aware Sessions
          - Single global session persists across all page navigations
          - Dynamic context injection based on current page route
          - Chat history maintained while context updates per-page */}
      <FloatingChatProvider>
        <OnboardingChecklistProvider>
          <AppContent />
        </OnboardingChecklistProvider>
      </FloatingChatProvider>
    </MuiThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthErrorBoundary>
        <Router>
          <AuthProvider>
            <ThemeProvider>
              <CurrencyProvider>
                <LoginDialogProvider>
                  <DiscoveryQuizProvider>
                    <ThemedApp />
                  </DiscoveryQuizProvider>
                </LoginDialogProvider>
              </CurrencyProvider>
            </ThemeProvider>
          </AuthProvider>
        </Router>
      </AuthErrorBoundary>
    </ErrorBoundary>
  );
};

export default App;
